import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // On app load, verify stored token is still valid
    const token = localStorage.getItem('pulsoToken');
    const storedUser = localStorage.getItem('pulsoUser');

    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
        setIsAuthenticated(true);
      } catch {
        localStorage.removeItem('pulsoToken');
        localStorage.removeItem('pulsoUser');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        return { success: false, message: data.error || 'Invalid email or password' };
      }

      // Store token and user
      localStorage.setItem('pulsoToken', data.token);
      localStorage.setItem('pulsoUser', JSON.stringify(data.user));

      setUser(data.user);
      setIsAuthenticated(true);

      return { success: true };
    } catch (err) {
      return { success: false, message: 'Could not connect to server. Please try again.' };
    }
  };

  /**
   * Log a user in given a token + user object (e.g. after accepting an invite).
   * Mirrors `login` but skips the email/password call.
   */
  const loginWithToken = (token, userObj) => {
    localStorage.setItem('pulsoToken', token);
    localStorage.setItem('pulsoUser', JSON.stringify(userObj));
    setUser(userObj);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem('pulsoToken');
    localStorage.removeItem('pulsoUser');
    setUser(null);
    setIsAuthenticated(false);
  };

  // Helper to get auth header for API calls throughout the app
  const getAuthHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem('pulsoToken')}`
  });

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, loginWithToken, logout, loading, getAuthHeader }}>
      {children}
    </AuthContext.Provider>
  );
};